import React from 'react';
import Icon from '@/components/AppIcon';
import Button from '@/components/ui/Button';
import EmployeeAvatar from '@/components/employees/EmployeeAvatar';
import useTranslation from '@/hooks/useTranslation';
import formatTranslation from '@/utils/formatTranslation';

const EmployeeCard = ({
  employee,
  onViewDetails,
  onMessage,
  onEdit,
  showActions = true,
  isCurrentUser = false,
}) => {
  const { t } = useTranslation(['employeeDirectory', 'common']);

  const getStatusColor = (status) => {
    switch (status) {
      case 'on-work':
        return 'bg-success';
      case 'off-work':
        return 'bg-muted-foreground';
      case 'in-vacation':
        return 'bg-warning';
      case 'holiday':
        return 'bg-primary';
      case 'sick-leave':
        return 'bg-error';
      default:
        return 'bg-muted-foreground';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'on-work':
        return t('employeeDirectory:statuses.onWork', 'On Work');
      case 'off-work':
        return t('employeeDirectory:statuses.offWork', 'Off Work');
      case 'in-vacation':
        return t('employeeDirectory:statuses.inVacation', 'In Vacation');
      case 'holiday':
        return t('employeeDirectory:statuses.holiday', 'Holiday');
      case 'sick-leave':
        return t('employeeDirectory:statuses.sickLeave', 'Sick Leave');
      default:
        return t('employeeDirectory:statuses.unknown', 'Unknown');
    }
  };

  const displayName =
    employee?.name ?? t('employeeDirectory:card.unknownName', 'Employee');
  const avatarAlt = formatTranslation(
    t('employeeDirectory:card.avatarAlt', '{{name}} profile photo'),
    { name: displayName }
  );

  return (
    <div className="relative bg-card border border-border rounded-lg p-4 hover:shadow-elevation-2 transition-smooth">
      {isCurrentUser && (
        <div className="absolute top-6 right-16">
          <span className="inline-flex items-center rounded-full bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground shadow-sm">
            {t('employeeDirectory:card.currentUserBadge', 'This is you')}
          </span>
        </div>
      )}
      <div className="flex items-start space-x-4">
        {/* Profile Image */}
        <EmployeeAvatar
          url={employee?.avatarUrl}
          name={employee?.name}
          alt={avatarAlt}
          status={employee?.status}
          size="lg"
          wrapperClassName="border border-border"
        />

        {/* Employee Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-2">
                <h3 className="text-lg font-semibold text-foreground truncate">
                  {employee?.name}
                </h3>
                {employee?.isManager && (
                  <Icon
                    name="Crown"
                    size={16}
                    className="text-accent flex-shrink-0"
                  />
                )}
              </div>
              <p className="text-sm text-muted-foreground">{employee?.role}</p>
              <p className="text-sm text-muted-foreground">
                {employee?.department}
              </p>
            </div>

            <div className="flex items-center space-x-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onEdit?.(employee)}
                className="flex-shrink-0"
              >
                <Icon
                  name="Pencil"
                  size={16}
                  className="text-muted-foreground"
                />
              </Button>
            </div>
          </div>

          {/* Location and Status */}
          <div className="flex items-center space-x-4 mt-2">
            <div className="flex items-center space-x-1">
              <div
                className={`w-2 h-2 ${getStatusColor(
                  employee?.status
                )} rounded-full`}
              ></div>
              <span className="text-sm text-muted-foreground">
                {getStatusText(employee?.status)}
              </span>
            </div>
          </div>

          {/* Contact Info */}
          <div className="flex flex-col items-left mt-2 space-y-1">
            <div className="flex items-center space-x-3 min-w-0">
              <Icon
                name="Phone"
                size={14}
                className="text-muted-foreground flex-shrink-0"
              />
              <span className="text-sm text-muted-foreground break-words w-full">
                {employee?.phone}
              </span>
            </div>
            <div className="flex items-center space-x-3 min-w-0">
              <Icon
                name="Mail"
                size={14}
                className="text-muted-foreground flex-shrink-0"
              />
              <span className="text-sm text-muted-foreground break-all w-full">
                {employee?.email}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          {showActions && (
            <div className="flex items-center space-x-2 mt-3">
              {isCurrentUser ? (
                <span className="flex-1"></span>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onMessage(employee)}
                  className="flex-1"
                >
                  <Icon name="MessageSquare" size={14} className="mr-1" />
                  {t('employeeDirectory:card.actions.message', 'Message')}
                </Button>
              )}
              <Button
                variant="default"
                size="sm"
                onClick={() => onViewDetails(employee)}
                className={isCurrentUser ? 'flex-2' : undefined}
              >
                {t(
                  'employeeDirectory:card.actions.viewDetails',
                  'View Details'
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeCard;
